import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllTasks, updateTask, deleteTask, EventTask, UpdateEventTaskPayload } from '../api/event-tasks';
import { apiClient } from '../api/client';
import { CreateEventTaskModal } from '../components/CreateEventTaskModal';

// ── Constants ─────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ToDo', label: 'To Do' },
  { value: 'InProgress', label: 'In Progress' },
  { value: 'Done', label: 'Done' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'Weekend', label: 'Weekend' },
  { value: 'Planning', label: 'Planning' },
  { value: 'General', label: 'General' },
  { value: 'Other', label: 'Other' },
];

const STATUS_COLORS: Record<string, string> = {
  ToDo: '#6b7280',
  InProgress: '#f57c00',
  Done: '#1a6b3c',
  Cancelled: '#d32f2f',
};

const PRIORITY_COLORS: Record<string, string> = {
  High: '#d32f2f',
  Medium: '#f57c00',
  Low: '#6b7280',
};

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: 0 },
  countBadge: { fontSize: '0.9rem', color: '#6b7280', fontWeight: 400 },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  filterBar: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem', color: '#333' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  statusSelect: { padding: '0.25rem 0.4rem', border: '1px solid #ccc', borderRadius: '3px', fontSize: '0.8rem', cursor: 'pointer' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1, transition: 'background-color 0.15s' },
  // Modal styles
  overlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  modal: { backgroundColor: '#fff', borderRadius: '8px', padding: '2rem', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' as const, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { color: '#1a6b3c', fontSize: '1.25rem', margin: '0 0 1.5rem 0' },
  formGroup: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '4px' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, minHeight: '80px', resize: 'vertical' as const },
  formSelect: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' },
  btnPrimary: { padding: '0.5rem 1.25rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '0.5rem 1.25rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { padding: '0.5rem 1.25rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
};

// ── Edit Modal Component ──────────────────────────────────────

interface EditTaskModalProps {
  task: EventTask;
  members: { id: string; firstName: string; lastName: string }[];
  onSave: (taskId: string, payload: UpdateEventTaskPayload) => Promise<void>;
  onDelete: (taskId: string) => Promise<void>;
  onClose: () => void;
}

function EditTaskModal({ task, members: _members, onSave, onDelete, onClose }: EditTaskModalProps) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [status, setStatus] = useState(task.status);
  const [priority, setPriority] = useState(task.priority);
  const [category, setCategory] = useState(task.category);
  const [eventId, setEventId] = useState(task.event?.id || '');
  const [events, setEvents] = useState<{ id: string; name: string }[]>([]);
  const [dueDate, setDueDate] = useState(task.dueDate ? task.dueDate.split('T')[0] : '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Multi-assignee management
  const [assignees, setAssignees] = useState<{ id: string; firstName: string; lastName: string; roleId?: string; roleName?: string }[]>([]);
  const [assigneeInput, setAssigneeInput] = useState('');
  const [assigneeRoleInput, setAssigneeRoleInput] = useState('');
  const [assigningMember, setAssigningMember] = useState(false);
  const [subcommitteeMembers, setSubcommitteeMembers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [weekendTaskRoles, setWeekendTaskRoles] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    fetchAssignees();
    apiClient<{ id: string; name: string }[]>('/events')
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]));
    apiClient<{ id: string; name: string }[]>('/weekend-task-roles')
      .then((data) => setWeekendTaskRoles(Array.isArray(data) ? data : []))
      .catch(() => setWeekendTaskRoles([]));
    // Fetch subcommittee members for scoped assignee dropdown
    if (task.subcommittee?.id) {
      apiClient<Array<{ member: { id: string; firstName: string; lastName: string } }>>(`/subcommittees/${task.subcommittee.id}/affiliations`)
        .then((data) => {
          if (Array.isArray(data)) {
            const scMembers = data.map((a) => a.member).filter(Boolean);
            setSubcommitteeMembers(scMembers);
          }
        })
        .catch(() => setSubcommitteeMembers([]));
    }
  }, []);

  async function fetchAssignees() {
    try {
      const data = await apiClient<{ id: string; firstName: string; lastName: string }[]>(`/event-tasks/${task.id}/assignees`);
      setAssignees(data);
    } catch {
      // Non-critical
    }
  }

  function getAvailableMembers() {
    const assignedIds = new Set(assignees.map((a) => a.id));
    return subcommitteeMembers.filter((m) => !assignedIds.has(m.id));
  }

  async function handleAssignMember() {
    if (!assigneeInput) return;
    setAssigningMember(true);
    setErrorMsg('');
    try {
      await apiClient(`/event-tasks/${task.id}/assignees`, {
        method: 'POST',
        body: { memberId: assigneeInput },
      });
      await fetchAssignees();
      setAssigneeInput('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setErrorMsg(apiErr.message || 'Failed to assign member.');
    } finally {
      setAssigningMember(false);
    }
  }

  async function handleRemoveAssignee(memberId: string) {
    try {
      await apiClient(`/event-tasks/${task.id}/assignees/${memberId}`, { method: 'DELETE' });
      setAssignees((prev) => prev.filter((a) => a.id !== memberId));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setErrorMsg(apiErr.message || 'Failed to remove assignee.');
    }
  }

  async function handleSave() {
    setSaving(true);
    setErrorMsg('');
    try {
      const payload: UpdateEventTaskPayload = {};
      if (title !== task.title) payload.title = title;
      if (description !== (task.description || '')) payload.description = description;
      if (status !== task.status) payload.status = status;
      if (priority !== task.priority) payload.priority = priority;
      if (category !== task.category) payload.category = category;
      if (eventId !== (task.event?.id || '')) payload.eventId = eventId || undefined;
      if (dueDate !== (task.dueDate ? task.dueDate.split('T')[0] : '')) payload.dueDate = dueDate;

      await onSave(task.id, payload);
      onClose();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setErrorMsg(apiErr.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    setErrorMsg('');
    try {
      await onDelete(task.id);
      onClose();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setErrorMsg(apiErr.message || 'Failed to delete task.');
    } finally {
      setDeleting(false);
    }
  }

  const availableMembers = getAvailableMembers();

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>Edit Task</h2>

        {errorMsg && <div style={styles.error}>{errorMsg}</div>}

        <div style={styles.formGroup}>
          <label style={styles.label}>Title</label>
          <input
            style={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            aria-label="Task title"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Description</label>
          <textarea
            style={styles.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            aria-label="Task description"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Status</label>
            <select style={styles.formSelect} value={status} onChange={(e) => setStatus(e.target.value as EventTask['status'])} aria-label="Status">
              <option value="ToDo">To Do</option>
              <option value="InProgress">In Progress</option>
              <option value="Done">Done</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Priority</label>
            <select style={styles.formSelect} value={priority} onChange={(e) => setPriority(e.target.value as EventTask['priority'])} aria-label="Priority">
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Due Date</label>
            <input
              type="date"
              style={styles.input}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              aria-label="Due date"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Assignees</label>
            {assignees.length > 0 && (
              <ul style={{ margin: '0 0 0.5rem 0', padding: '0', fontSize: '0.85rem', listStyle: 'none' }}>
                {assignees.map((a) => (
                  <li key={a.id} style={{ marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ minWidth: '100px' }}>{a.firstName} {a.lastName}</span>
                    <select
                      value={(a as any).roleId || ''}
                      onChange={async (e) => {
                        try {
                          await apiClient(`/event-tasks/${task.id}/assignees`, {
                            method: 'POST',
                            body: { memberId: a.id, roleId: e.target.value || null },
                          });
                          await fetchAssignees();
                        } catch { /* silent */ }
                      }}
                      style={{ fontSize: '0.78rem', padding: '2px 6px', borderRadius: '4px', border: '1px solid #ccc', maxWidth: '160px' }}
                      aria-label={`Role for ${a.firstName} ${a.lastName}`}
                    >
                      <option value="">No role</option>
                      {weekendTaskRoles.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#dc2626', padding: '2px 4px' }}
                      onClick={() => handleRemoveAssignee(a.id)}
                      title="Remove assignee"
                      aria-label={`Remove ${a.firstName} ${a.lastName}`}
                    >✕</button>
                  </li>
                ))}
              </ul>
            )}
            {availableMembers.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <select style={{ ...styles.formSelect, flex: 1, minWidth: '120px' }} value={assigneeInput} onChange={(e) => setAssigneeInput(e.target.value)} aria-label="Add assignee">
                  <option value="">— Add assignee —</option>
                  {availableMembers.map((m) => (
                    <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                  ))}
                </select>
                <select style={{ ...styles.formSelect, maxWidth: '150px' }} value={assigneeRoleInput} onChange={(e) => setAssigneeRoleInput(e.target.value)} aria-label="Role for new assignee">
                  <option value="">No role</option>
                  {weekendTaskRoles.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                <button style={styles.btnSecondary} onClick={handleAssignMember} disabled={!assigneeInput || assigningMember}>+</button>
              </div>
            )}
          </div>
        </div>

        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
          Subcommittee: {task.subcommittee?.name || '—'}
          {task.event && <> &bull; Event: {task.event.name}</>}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Category</label>
          <select style={styles.formSelect} value={category} onChange={(e) => setCategory(e.target.value as EventTask['category'])} aria-label="Category">
            <option value="Weekend">Weekend</option>
            <option value="Planning">Planning</option>
            <option value="General">General</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Event</label>
          <select style={styles.formSelect} value={eventId} onChange={(e) => setEventId(e.target.value)} aria-label="Event">
            <option value="">— No Event —</option>
            {events.map((evt) => (
              <option key={evt.id} value={evt.id}>{evt.name}</option>
            ))}
          </select>
        </div>

        <div style={styles.modalActions}>
          <button style={styles.btnDanger} onClick={handleDelete} disabled={deleting}>
            {deleting ? '...' : '🗑️ Delete'}
          </button>
          <div style={{ flex: 1 }} />
          <button style={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page Component ───────────────────────────────────────

export function AllTasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<EventTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcommitteeFilter, setSubcommitteeFilter] = useState('');
  const [subcommittees, setSubcommittees] = useState<{ id: string; name: string }[]>([]);

  // Edit modal
  const [editingTask, setEditingTask] = useState<EventTask | null>(null);
  const [members, setMembers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchSubcommittees();
    fetchMembers();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [statusFilter, categoryFilter]);

  async function fetchSubcommittees() {
    try {
      const data = await apiClient<{ id: string; name: string }[]>('/subcommittees');
      setSubcommittees(data);
    } catch {
      // Non-critical
    }
  }

  async function fetchMembers() {
    try {
      const data = await apiClient<{ id: string; firstName: string; lastName: string }[]>('/members');
      setMembers(data);
    } catch {
      // Non-critical — assignee dropdown will be empty
    }
  }

  async function fetchTasks() {
    setLoading(true);
    setError('');
    try {
      const filters: { status?: string; category?: string } = {};
      if (statusFilter) filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;
      const data = await getAllTasks(filters);
      setTasks(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(taskId: string, newStatus: string) {
    try {
      await updateTask(taskId, { status: newStatus });
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: newStatus as EventTask['status'] } : t)),
      );
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update task status.');
    }
  }

  async function handleSaveTask(taskId: string, payload: UpdateEventTaskPayload) {
    await updateTask(taskId, payload);
    await fetchTasks(); // Refresh to get updated data
  }

  async function handleDeleteTask(taskId: string) {
    await deleteTask(taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }

  // Client-side subcommittee filter
  const filteredTasks = subcommitteeFilter
    ? tasks.filter((t) => t.subcommittee?.id === subcommitteeFilter)
    : tasks;

  // ── Helpers ───────────────────────────────────────────────

  function formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function getAssigneeName(task: EventTask): string {
    if (!task.assignee) return '—';
    return `${task.assignee.firstName} ${task.assignee.lastName}`;
  }

  // ── Render ────────────────────────────────────────────────

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          All Event Tasks{' '}
          <span style={styles.countBadge}>({filteredTasks.length})</span>
        </h1>
        <button
          style={styles.btnPrimary}
          onClick={() => setShowCreateModal(true)}
        >
          + Create Task
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.section}>
        {/* Filter Bar */}
        <div style={styles.filterBar}>
          <select
            style={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            style={styles.select}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label="Filter by category"
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            style={styles.select}
            value={subcommitteeFilter}
            onChange={(e) => setSubcommitteeFilter(e.target.value)}
            aria-label="Filter by subcommittee"
          >
            <option value="">All Subcommittees</option>
            {subcommittees.map((sc) => (
              <option key={sc.id} value={sc.id}>{sc.name}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div style={styles.loading}>Loading tasks...</div>
        ) : filteredTasks.length === 0 ? (
          <div style={styles.emptyState}>No tasks found.</div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Subcommittee</th>
                  <th style={styles.th}>Assignee</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr key={task.id}>
                    <td style={styles.td}>
                      <strong
                        style={{ cursor: 'pointer', color: '#1a6b3c' }}
                        onClick={() => navigate(`/all-tasks/${task.id}`)}
                        role="link"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/all-tasks/${task.id}`); }}
                        aria-label={`View details for ${task.title}`}
                      >
                        {task.title}
                      </strong>
                      {task.event && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                          📅 {task.event.name}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{task.subcommittee?.name || '—'}</td>
                    <td style={styles.td}>{getAssigneeName(task)}</td>
                    <td style={styles.td}>{task.category}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          backgroundColor: PRIORITY_COLORS[task.priority] || '#6b7280',
                        }}
                      >
                        {task.priority}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(task.dueDate)}</td>
                    <td style={styles.td}>
                      <select
                        style={{
                          ...styles.statusSelect,
                          color: STATUS_COLORS[task.status] || '#333',
                          fontWeight: 600,
                        }}
                        value={task.status}
                        onChange={(e) => handleStatusChange(task.id, e.target.value)}
                        aria-label={`Change status for ${task.title}`}
                      >
                        <option value="ToDo">To Do</option>
                        <option value="InProgress">In Progress</option>
                        <option value="Done">Done</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.iconBtn}
                        onClick={() => navigate(`/all-tasks/${task.id}`)}
                        title="Edit task"
                        aria-label={`Edit ${task.title}`}
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingTask && (
        <EditTaskModal
          task={editingTask}
          members={members}
          onSave={handleSaveTask}
          onDelete={handleDeleteTask}
          onClose={() => setEditingTask(null)}
        />
      )}

      {/* Create Task Modal */}
      <CreateEventTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => { setShowCreateModal(false); fetchTasks(); }}
      />
    </div>
  );
}
